import { useState, useEffect } from 'react';
import { syncService } from '../services/offline/sync';

interface OfflineStatus {
  isOnline: boolean;
  isSyncing: boolean;
  pendingCount: number;
  lastSyncTime: number | null;
}

export function useOffline(): OfflineStatus {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [syncStatus, setSyncStatus] = useState({
    isSyncing: false,
    pendingCount: 0,
    lastSyncTime: null as number | null,
  });

  useEffect(() => {
    // Update online status
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Subscribe to sync status
    const unsubscribe = syncService.subscribe((status) => {
      setSyncStatus({
        isSyncing: status.isSyncing,
        pendingCount: status.pendingCount,
        lastSyncTime: status.lastSyncTime,
      });
    });

    // Get initial pending count
    syncService.getPendingCount().then((count) => {
      setSyncStatus((prev) => ({ ...prev, pendingCount: count }));
    });

    // Start auto-sync
    syncService.startAutoSync(30000); // Every 30 seconds

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      unsubscribe();
      syncService.stopAutoSync();
    };
  }, []);

  return {
    isOnline,
    ...syncStatus,
  };
}

// Hook for checking if we should use offline mode
export function useShouldUseOffline(): boolean {
  const { isOnline } = useOffline();
  return !isOnline;
}
